import React, { useMemo, useState, useEffect } from 'react';
import { Auth } from '~/amplify';
import AnimatedBlock from '~/components/atom/AnimatedBlock';
import { Body, Label } from '~/components/atom/Typography';
import JustificationContainer from '~/components/atom/JustificationContainer';
import ToggleCheckbox from '~/components/molecule/ToggleCheckbox';
import useConfirmModal from '~/hooks/useConfirmModal';
import useAddToast from '~/hooks/useAddToast';
import formatToastMessage from '~/util/formatToastMessage';
import Catalog from '~/Catalog';
import { useUserProfileSyncMutation } from '~/graphql/types';
import useCurrentAccount from '~/hooks/useCurrentAccount';

type Props = {
  mfaRequired: boolean;
  onSave: (value: boolean) => Promise<void>;
  loading: boolean;
  disabled: boolean;
};

const text = {
  title: 'Tweestapsverificatie vereisen',
  description:
    'Schakel in om alle gebruikers tweestapsverificatie te laten instellen voor toegang.',
  enableLabel: 'Tweestapsverificatie vereist voor alle gebruikers',
  disabledExplanation:
    'Alleen accountbeheerders kunnen deze instelling wijzigen.',
  confirmTitleEnable: 'Tweestapsverificatie activeren',
  confirmTitleDisable: 'Tweestapsverificatie deactiveren',
  confirmTitleEnableDescriptionForNoMfa:
    'Om tweestapsverificatie voor alle gebruikers te vereisen, dien je eerst zelf tweestapsverificatie te activeren. Je wordt doorgestuurd naar de activatie wizard.',
  confirmMessageEnable:
    'Tweestapsverificatie wordt vereist voor alle gebruikers.',
  confirmMessageDisable: 'Tweestapsverificatie wordt niet meer vereist.',
  confirmButton: 'Bevestigen',
};

const MFARequiredBlock: React.FC<Props> = ({
  mfaRequired,
  onSave,
  loading,
  disabled,
}) => {
  const { id: accountId } = useCurrentAccount();
  const [localValue, setLocalValue] = useState(mfaRequired);
  const [pendingValue, setPendingValue] = useState<boolean | null>(null);
  const [adminHasMfaSetup, setAdminHasMfaSetup] = useState<boolean | null>(
    null,
  );
  const [userProfileSync] = useUserProfileSyncMutation({
    variables: { accountId },
  });

  const addToast = useAddToast();

  useEffect(() => {
    if (disabled) return;
    // Do the sync in case it is not up to date
    void userProfileSync();
    setLocalValue(mfaRequired);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mfaRequired, disabled]);

  useEffect(() => {
    let cancelled = false;

    const loadAdminMfaSetup = async () => {
      try {
        const preferences = await Auth.fetchMFAPreference();
        const hasMfa = preferences.enabled?.includes('TOTP') ?? false;

        if (!cancelled) setAdminHasMfaSetup(hasMfa);
      } catch {
        if (!cancelled) setAdminHasMfaSetup(null);
      }
    };

    void loadAdminMfaSetup();

    return () => {
      cancelled = true;
    };
  }, []);

  const confirmModalLabels = useMemo(() => {
    const showMfaSetupWarning =
      pendingValue === true && adminHasMfaSetup === false;
    const isEnablingMfa = pendingValue === true;

    return {
      title: isEnablingMfa ? text.confirmTitleEnable : text.confirmTitleDisable,
      message: (
        <>
          {showMfaSetupWarning ? (
            <Body>{text.confirmTitleEnableDescriptionForNoMfa}</Body>
          ) : (
            <Body>
              {isEnablingMfa
                ? text.confirmMessageEnable
                : text.confirmMessageDisable}
            </Body>
          )}
        </>
      ),
    };
  }, [adminHasMfaSetup, pendingValue]);

  const { modal: confirmModal, setShowModal: setShowConfirmModal } =
    useConfirmModal({
      labels: confirmModalLabels,
      buttons: [
        {
          label: text.confirmButton,
          appearance: 'primary',
          onClick: async () => {
            if (pendingValue == null) return;

            try {
              setLocalValue(pendingValue);
              await onSave(pendingValue);
            } catch {
              addToast([
                formatToastMessage(
                  Catalog.genericUnknownErrorMessage,
                  'danger',
                ),
              ]);
              setLocalValue(mfaRequired);
            } finally {
              setPendingValue(null);
            }
          },
        },
      ],
      level: 'warning',
      onCancel: () => setPendingValue(null),
    });

  const handleToggle = (e: React.FormEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    setPendingValue(target.checked);
    setShowConfirmModal(true);
  };

  return (
    <AnimatedBlock title={text.title} dataTestId="mfa-required-block">
      {confirmModal}
      <JustificationContainer direction="column" gap="base">
        <Body margin={[null]}>{text.description}</Body>
        {disabled && <Label margin={[null]}>{text.disabledExplanation}</Label>}

        <ToggleCheckbox
          value={localValue}
          onChange={handleToggle}
          disabled={loading || disabled}
          label={text.enableLabel}
          containerProps={{
            direction: 'row',
            width: '100%',
            align: 'center',
            justification: 'space-between',
          }}
          size="small"
        />
      </JustificationContainer>
    </AnimatedBlock>
  );
};

export default MFARequiredBlock;
